import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useSession } from 'next-auth/react';

import {
  CHAT_AGENT,
  CHAT_AGENTS,
  ChatAgentName,
  chatPlaceholderKey,
  clearChatSessionStorage,
  isChatAgentName,
  sessionKey,
} from '@services/agent/chatAgents';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { useHeaderInfo } from '@hooks/features/useHeaderInfo';

/**
 * @description Rolling 3h chat sessions — one live session per agent, per user.
 *
 * A session is a `uuidv4()` thread id plus a locally stored expiry stamp. Either party sliding the
 * window refreshes the stamp (`touchSession`): the user sending, and the agent answering. So a
 * greeting the visitor never replies to still persists the session, and the next visit resumes that
 * conversation instead of paying for another opener.
 *
 * The resume-or-mint decision needs **no network** — it is a `localStorage` read and a clock
 * comparison. It does still land in an effect, so `threadId` is `null` for one render after auth
 * settles rather than being available during the first render. That window is why `submitChat` in
 * `BestieChatStateContext` returns early on `!threadId`: `useStreamStateV2` falls back to
 * `useAnonSessionId()` when handed a null thread id, which for a signed-in user is the legacy
 * permanent thread this feature replaces.
 *
 * Resolving during render instead (a lazy `useState` initialiser) would make the value available
 * immediately, but it would also run under SSR — minting a uuid server-side and risking a hydration
 * mismatch. The one-render wait plus the guard is the cheaper trade.
 *
 * Why the expiry is local rather than the server's `last_message_at`: the thread id never leaves
 * this device, so web and iOS mint independent uuids and are never on the same session — there is
 * nothing for a server timestamp to be authoritative about. Deriving the TTL from a fetch would mean
 * the client could not know whether to resume until that fetch landed, which forces a pending state,
 * a race against the user's first send, and a discard path for history committed before the verdict.
 * The only cost is device-clock skew, and it is mild both ways: a clock jumped forward ends the
 * session early (fresh thread, nothing lost); jumped backward resumes an older thread of the user's
 * own. Ownership is still enforced server-side on `user_id`.
 *
 * **Why the agent is in the value, not the key.** One record per user (`chat-session:{userId}`) holds
 * a map of `agent -> session`. Agents are DB configuration on the service side, so the set can change
 * under a shipped client — an agent gets renamed, replaced, or joined by a second one. With the agent
 * in the key, every such change is a key-format change: live sessions orphan and a prune of unknown
 * keys has to ship with it. With the agent in the value the format never moves; entries for agents
 * nobody talks to any more simply age out, because `parseStoredSessions` drops expired entries on
 * every read and `writeSession` persists the pruned map.
 */

export interface StoredSession {
  threadId: string;
  /** Epoch ms. Refreshed to `now + ttl` on every message sent — this is the sliding window. */
  expiresAt: number;
}

/** One user's live sessions, keyed by the agent each one belongs to. */
export type StoredSessions = Partial<Record<ChatAgentName, StoredSession>>;

/**
 * @description Read a user's stored sessions, tolerating anything malformed and pruning anything dead.
 *
 * Per-entry validation rather than all-or-nothing: one hand-edited or truncated agent entry must not
 * cost the user their other live session. Entries whose `expiresAt` has passed are dropped here too,
 * which is what keeps records for retired agents from accumulating — `writeSession` stores back
 * whatever this returns.
 *
 * Also accepts a legacy flat `{ threadId, expiresAt }` record, reading it as the current agent's
 * session, so an in-flight conversation survives the upgrade to the map format.
 */
export const parseStoredSessions = (
  raw: string | null,
  now: number,
  currentAgent: ChatAgentName = CHAT_AGENT,
): StoredSessions => {
  if (!raw) return {};

  const isLive = (value: unknown): value is StoredSession => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const { threadId, expiresAt } = value as Partial<StoredSession>;
    if (typeof threadId !== 'string' || !threadId) return false;
    if (typeof expiresAt !== 'number' || !Number.isFinite(expiresAt)) return false;
    // Strict `>`: a session is alive only while there is time left on it.
    return expiresAt > now;
  };

  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};

    // Legacy shape, written before sessions were held per agent.
    if (isLive(parsed)) return { [currentAgent]: parsed } as StoredSessions;

    const sessions: StoredSessions = {};
    Object.entries(parsed as Record<string, unknown>).forEach(([agent, value]) => {
      if (!isChatAgentName(agent)) return;
      if (!isLive(value)) return;
      sessions[agent] = value;
    });

    return sessions;
  } catch {
    return {};
  }
};

/**
 * @description Resume-or-start decision for one agent. Pure, so the rules are testable without React
 * and can be mirrored one-for-one by the iOS `SessionStore`.
 *
 * There is no message-count guard because `expiresAt` is only ever written when a turn actually
 * happens — the user sends, or the agent answers. A stored, live record therefore already implies a
 * thread with something in it, including one that only holds the opener the visitor never replied to:
 * that greeting cost an LLM call, so resuming it is the point rather than a compromise.
 *
 * Expiry is not re-checked here: `parseStoredSessions` has already dropped anything dead, so an entry
 * that reaches this point is live by construction.
 */
export const decideSession = (
  { stored, agent, now }: { stored: StoredSessions; agent: ChatAgentName; now: number },
): { threadId: string | null; isResumed: boolean } => {
  const session = stored[agent];
  if (session && session.threadId && session.expiresAt > now) {
    return { threadId: session.threadId, isResumed: true };
  }
  return { threadId: null, isResumed: false };
};

/**
 * @description Whether the visitor is an employer. Selects the chat input's placeholder copy — see
 * `chatPlaceholderKey`. It does not select an agent.
 *
 * Two sources carry the same `hasBusinessWallet` field:
 *
 * - **The session user**, which *is* the login header-info payload (`baseAuth.ts:139-141`), so it is
 *   available the moment the visitor is authenticated — no request.
 * - **The Redux header-info slice**, filled by the Header's SWR fetch. Fresher (`useHeaderInfoData`
 *   self-heals the session from it), but it has not landed when the chat first resolves.
 *
 * Reading only the second one is why this was wrong before: on a fresh login the slice is still empty, so
 * every visitor looked like a non-employer. Prefer header info when present because it is authoritative,
 * and fall back to the session so a first login still gets the right copy.
 *
 * `undefined` means genuinely unknown; `chatPlaceholderKey` treats it as the candidate default, so a
 * missing field must not be coerced to `false` elsewhere.
 */
export const resolveIsEmployer = (
  sessionUser: { hasBusinessWallet?: boolean } | undefined,
  headerInfo: { hasBusinessWallet?: boolean } | undefined,
): boolean | undefined => {
  if (typeof headerInfo?.hasBusinessWallet === 'boolean') return headerInfo.hasBusinessWallet;
  if (typeof sessionUser?.hasBusinessWallet === 'boolean') return sessionUser.hasBusinessWallet;
  return undefined;
};

/** `localStorage` access wrapped so Safari private mode (which throws) falls back to in-memory. */
const readLocal = (key: string): string | null => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeLocal = (key: string, value: string): void => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage unavailable (Safari private mode, quota). The session still works for this page view;
    // it just will not be resumable. Failing quiet is correct — a chat must not break over this.
  }
};

/**
 * @description Stamp one agent's session, read-modify-write.
 *
 * Reads the whole record so the other agents' live sessions survive, and writes back the pruned map
 * `parseStoredSessions` returns — which is the only thing that ever removes a dead entry from storage.
 */
const writeSession = (
  userId: string,
  agent: ChatAgentName,
  threadId: string,
  now: number,
): void => {
  const sessions = parseStoredSessions(readLocal(sessionKey(userId)), now, agent);

  sessions[agent] = { threadId, expiresAt: now + CHAT_AGENTS[agent].sessionTtlMs };

  writeLocal(sessionKey(userId), JSON.stringify(sessions));
};

export interface UseChatSessionResult {
  /**
   * The session's thread id. `null` while NextAuth is loading, and for one render after it settles
   * while the effect resolves — callers that send must guard on it, see the hook doc above.
   */
  threadId: string | null;
  isResumed: boolean;
  isResolving: boolean;
  /**
   * Slide the window. Call on every turn — the user's send and the agent's reply alike. Nothing
   * else persists the session, so a missed call silently makes it unresumable.
   */
  touchSession: () => void;
}

export function useChatSession(agent: ChatAgentName = CHAT_AGENT): UseChatSessionResult {
  const { data: session, status } = useSession();
  const guestSessionId = useAnonSessionId();

  const userId = (session?.user as { userId?: number | string } | undefined)?.userId;
  const userKey = userId === undefined || userId === null ? null : String(userId);
  const isAuthenticated = status === 'authenticated' && !!userKey;

  // Resolve once per agent+user. Keyed rather than mount-scoped so a NextAuth token refresh cannot
  // rotate the thread mid-conversation, while a genuine account or agent change does re-resolve —
  // without the agent in the key, the render after an agent change pairs the new agent with the old
  // thread id.
  const resolvedForRef = useRef<string | null>(null);
  const [resolved, setResolved] = useState<
  { key: string; threadId: string; isResumed: boolean } | null
  >(null);

  const resolutionKey = isAuthenticated && userKey ? `${agent}:${userKey}` : null;

  useEffect(() => {
    if (!resolutionKey || !userKey) return;

    if (resolvedForRef.current === resolutionKey) return;
    resolvedForRef.current = resolutionKey;

    const now = Date.now();
    const decision = decideSession({
      stored: parseStoredSessions(readLocal(sessionKey(userKey)), now, agent),
      agent,
      now,
    });

    // A freshly minted thread is deliberately NOT persisted here — only `touchSession` writes, and
    // the caller calls it when a turn actually lands. A page view that never produces one (the agent
    // failed, the tab closed while the opener was in flight) therefore leaves no record, and the next
    // visit mints again rather than resuming a thread with nothing in it.
    setResolved({
      key: resolutionKey,
      threadId: decision.threadId ?? uuidv4(),
      isResumed: decision.isResumed,
    });
  }, [resolutionKey, userKey, agent]);

  // The effect above runs *after* the render that changed the user, so for one render `resolved` can
  // still hold the previous user's thread. Gate on the key so an unresolved user reports "still
  // resolving" rather than handing back someone else's thread id.
  const currentResolved = resolved?.key === resolutionKey ? resolved : null;

  const touchSession = useCallback(() => {
    if (!userKey || !currentResolved) return;

    writeSession(userKey, agent, currentResolved.threadId, Date.now());
  }, [userKey, agent, currentResolved]);

  return useMemo(() => {
    if (status === 'loading') {
      return {
        threadId: null, isResumed: false, isResolving: true, touchSession,
      };
    }

    if (!isAuthenticated) {
      // Guests keep exactly today's behaviour: one permanent cookie uuid, no rolling session.
      return {
        threadId: guestSessionId, isResumed: false, isResolving: false, touchSession,
      };
    }

    return {
      threadId: currentResolved?.threadId ?? null,
      isResumed: currentResolved?.isResumed ?? false,
      isResolving: !currentResolved,
      touchSession,
    };
  }, [status, isAuthenticated, guestSessionId, currentResolved, touchSession]);
}

/**
 * @description The Loco key for the chat input's placeholder, chosen by audience.
 *
 * Lives here rather than in the component so the two sources of `hasBusinessWallet` are read in one
 * place — see `resolveIsEmployer` for why both are needed.
 */
export function useChatPlaceholderKey(): string {
  const { data: session } = useSession();
  const headerInfo = useHeaderInfo();

  return chatPlaceholderKey(resolveIsEmployer(session?.user, headerInfo));
}

/**
 * @description Wipe the chat and reload when the visitor stops being signed in.
 *
 * Signing out has to take the transcript with it. Without this the previous user's conversation stays
 * on screen — the provider keeps its `stream` state, and neither the thread id changing nor
 * `hasHistoryLoaded` resetting removes messages already rendered. On a shared device that is someone
 * else's conversation left visible.
 *
 * The burger-menu logout hard-navigates to `/`, so it never had the problem. The path that does is a
 * sign-out the client never asked for: `app/api/proxy/[...proxy]/route.ts` calls `signOut()`
 * **server-side** on a 498 and deliberately does not redirect ("the middleware dead-session heal
 * covers those on the next navigation"), so the session dies underneath a mounted chat. Reacting to
 * the status transition here covers that and any future logout entry point, instead of trusting each
 * one to clean up.
 *
 * A full reload rather than a state reset, because the chat is not the only thing holding user data —
 * Redux, SWR caches and the stream all need to go, and reload is the one operation that cannot miss
 * any of them.
 */
export function useResetChatOnSignOut(): void {
  const { status } = useSession();
  const wasAuthenticatedRef = useRef(false);

  useEffect(() => {
    if (status === 'authenticated') {
      wasAuthenticatedRef.current = true;
      return;
    }

    // Only react to a real transition. `status` starts as 'loading' and settles to 'unauthenticated'
    // for every guest, which must not trigger a reload — and after the reload below the ref starts
    // false again, so this cannot loop.
    if (status === 'unauthenticated' && wasAuthenticatedRef.current) {
      wasAuthenticatedRef.current = false;
      clearChatSessionStorage();
      window.location.reload();
    }
  }, [status]);
}
